import { createLookup, isArrayEmpty, isNumber, isUndefinedOrNull, selectMany, SsmClassification } from "../omnia"
import { SsmAttribute, SsmAttributeGroup } from "./types"

/**
 * Returns all SSM attributes as a map index by property name
 */
export function getSsmAttributesAsMap(classification: SsmClassification[], keySelector: (a: SsmAttribute) => string = a => a.property) {

    const groups = getSsmAttributesGrouped(classification)

    const attrs: SsmAttribute[] = selectMany(Object.keys(groups).map(k => groups[k]))

    const map = new Map(attrs.map(a => [keySelector(a), a]))

    return map

}

/**
 * Provides a list of the attributes of an SSM analysis (not including generic analysis attributes such as number, date, field...)
 * The attributes are grouped in five predefined groups
 * The function expects the default classification for all SSM properties, a subset of which is included in each returned attribute
 */
export function getSsmAttributesGrouped(classification: SsmClassification[]): { [group in SsmAttributeGroup]: SsmAttribute[] } {

    const lookup = createLookup(classification || [], c => c.Property)

    function setupGroup(group: SsmAttributeGroup, attrs: Omit<SsmAttribute, "group" | "classes" | "hasClassification" | "getClass">[]) {

        return attrs.map(a => {

            let classes = (lookup[a.ssmProperty || a.property] || [])

            const hasClassification = classes.length > 0

            classes = classes.sort((a, b) => a.MinValue - b.MinValue)

            const orderedClasses = [...classes].reverse()

            return {
                ...a,
                group,
                classes,
                hasClassification,
                getClass: (value: number) => (!hasClassification)
                    ? undefined
                    : getSsmClass(value, orderedClasses)
            }
        })


    }

    return {

        std: setupGroup("std", [
            { property: "P2O5", name: "P\u2082O\u2085", unit: "mg/l", gaugeColorKey: "P2O5", required: true, min: 0 },
            { property: "K2O", name: "K\u2082O", unit: "mg/l", gaugeColorKey: "K2O", required: true, min: 10, max: 3000 },
            { property: "MgO", name: "MgO", unit: "mg/l", gaugeColorKey: "MgO", required: true, min: 10, max: 1250 },
            { property: "WaterpH", name: "Soil pH", unit: undefined, gaugeColorKey: "pH", inputStep: 0.1, ssmProperty: "SoilPh", required: true, min: 4, max: 9 },
            { property: "BufferPh", name: "Buffer pH", unit: undefined, gaugeColorKey: "pH", inputStep: 0.1, required: true, min: 4, max: 9 },
        ]),

        soil: setupGroup("soil", [
            { property: "Sand", name: "Sand", unit: "%", factor: 100, min: 0, max: 1 },
            { property: "Silt", name: "Silt", unit: "%", factor: 100, min: 0, max: 1 },
            { property: "Clay", name: "Clay", unit: "%", factor: 100, min: 0, max: 1 },
            { property: "DryBulkDensity", name: "Dry Bulk Density", unit: undefined, inputStep: 0.01 },
            { property: "SoilCEC", name: "CEC", unit: "meq/100g" },
            { property: "OrganicMatterLoi", name: "Organic Matter (LOI)", unit: "%", factor: 100, inputStep: 0.1, min: 0, max: 1, gaugeColorKey: "OrganicMatter" },
            { property: "Carbon", name: "Carbon", unit: "%", inputStep: 0.1 },
            { property: "ActiveCarbon", name: "Active Carbon", unit: "%", inputStep: 0.1, min: 0, max: 100 },
            { property: "CO2Burst", name: "CO2 Burst", "unit": "mg/kg", min: 0 },
        ]),

        available: setupGroup("available", [
            { property: "AvailableCaO", name: "Available Calcium (CaO)", unit: "kg/ha" },
            { property: "AvailableMgO", name: "Available Magnesium (MgO)", unit: "kg/ha" },
            { property: "AvailableK2O", name: "Available Potash (K\u2082O)", unit: "kg/ha" },
            { property: "AvailableNa", name: "Available Sodium (Na)", unit: "kg/ha" },
            { property: "AvailableSO3", name: "Available Sulphur (SO\u2083)", unit: "kg/ha" },
            { property: "AvailableP2O5", name: "Available Phosphorus (P\u2082O\u2085)", unit: "kg/ha" },
        ]),

        total: setupGroup("total", [
            { property: "TotalCaO", name: "Total Calcium (CaO)", unit: "kg/ha" },
            { property: "TotalMgO", name: "Total Magnesium (MgO)", unit: "kg/ha" },
            { property: "TotalK2O", name: "Total Potash (K\u2082O)", unit: "kg/ha" },
            { property: "TotalNa", name: "Total Sodium (Na)", unit: "kg/ha" },
            { property: "TotalSO3", name: "Total Sulphur (SO\u2083)", unit: "kg/ha" },
            { property: "TotalP2O5", name: "Total Phosphorus (P\u2082O\u2085)", unit: "kg/ha" },
        ]),

        additional: setupGroup("additional", [
            { property: "Boron", name: "Boron", unit: "mg/l", inputStep: 0.1 },
            { property: "Iron", name: "Iron", unit: "mg/l" },
            { property: "Manganese", name: "Manganese", unit: "mg/l" },
            { property: "Copper", name: "Copper", unit: "mg/l", inputStep: 0.1 },
            { property: "Zinc", name: "Zinc", unit: "mg/l", inputStep: 0.1 },
            { property: "Chlorine", name: "Chlorine", unit: "mg/kg" },
            { property: "Cobalt", name: "Cobalt", unit: "mg/l", inputStep: 0.1 },
            { property: "Molybdenum", name: "Molybdenum", unit: "mg/l", inputStep: 0.1 },
        ])

    }
}

/**
 * Returns the class matching a specific value
 * The "classes" array must be sorted in descending MinValue order
 */
export function getSsmClass(value: number, classes: SsmClassification[]) {

    if (isArrayEmpty(classes)) {
        return undefined
    }

    if (isUndefinedOrNull(value)) {
        return undefined
    }

    let m = classes.find(c => c.MinValue <= value)

    if (!m) {

        m = value < classes[classes.length - 1].MinValue
            ? classes[classes.length - 1]
            : classes[0]

    }

    return m

}

/**
* Validates an attribute's value, returns a string indicating the error if invalid
*/
export function validateSsmValue(attr: SsmAttribute, value: number) {

    if (isUndefinedOrNull(value)) {

        return attr.required
            ? "This property is required"
            : undefined
    }

    const f: (value: number) => number = attr.factor ? v => v * attr.factor : v => v

    const min = isNumber(attr.min)
    const max = isNumber(attr.max)

    if (min && max && ((attr.min > value) || (attr.max < value))) {
        return `This property must in the [${f(attr.min)}, ${f(attr.max)}] range`
    }

    if (min && (attr.min > value)) {
        return `This property must >= ${f(attr.min)}`
    }

}
